"use client";

import Clarity from "@microsoft/clarity";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";

const CLARITY_MASK_SELECTORS = [
  // フォーム入力内容は常にマスクする
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  // 個人情報（名前/メールアドレス）を含みうる要素は明示的にマスクする
  "[autocomplete='name']",
  "[autocomplete='given-name']",
  "[autocomplete='additional-name']",
  "[autocomplete='family-name']",
  "[autocomplete='nickname']",
  "[autocomplete='email']",
  "[name*='name' i]",
  "[name*='email' i]",
  "[id*='name' i]",
  "[id*='email' i]",
  "[href^='mailto:']",
].join(",");

const applyClarityMask = (root: ParentNode) => {
  root.querySelectorAll(CLARITY_MASK_SELECTORS).forEach((element) => {
    element.setAttribute("data-clarity-mask", "true");
  });
};

/**
 * Microsoft Clarityを初期化するコンポーネント
 */
export function MicrosoftClarity() {
  const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
  const { ID } = useAuth();

  useEffect(() => {
    if (projectId) {
      Clarity.init(projectId);

      // Clarity管理画面側で全体マスクしている場合でも、必要な情報のみ取得できるようにする
      document.body.setAttribute("data-clarity-unmask", "true");
      applyClarityMask(document);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.matches(CLARITY_MASK_SELECTORS)) {
                element.setAttribute("data-clarity-mask", "true");
              }
              applyClarityMask(element);
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // TODO: EU対応でユーザーのCookie同意を取得して、同意していればClarity.consent(true)やClarity.setTagを呼び出す
      // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api#specify-if-cookies-should-be-set
      Clarity.consent(true);

      if (ID) {
        // カスタムユーザーIDを設定し、モバイルとデスクトップでサイトにアクセスした場合にも同一ユーザーとして扱う
        // https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api#customizing-custom-user-id

        // TODO: GA4 追加するときに、連携するため第二引数や第三引数を追加する
        Clarity.identify(ID);
      }

      return () => {
        observer.disconnect();
      };
    }

    return undefined;
  }, [projectId, ID]);

  return null;
}
